import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { LEGAL_DOCS, LEGAL_EMAIL, LEGAL_VERSION, type LegalKind } from "@/legal/content";

// Public legal page (no login). Italian text is binding; English is a verified translation; other UI languages fall back to English.
export function LegalPage({ kind }: { kind: LegalKind }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("it") ? "it" : "en";
  const doc = LEGAL_DOCS[kind][lang];
  const other: LegalKind = kind === "privacy" ? "terms" : "privacy";

  return (
    <Layout>
      <main data-testid={`legal-${kind}-page`} className="relative mx-auto max-w-3xl px-6 pb-32 pt-14 sm:px-10">
        <span className="inline-flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.34em] text-[#7FD4FF]">
          <span className="h-px w-8 bg-gradient-to-r from-[#2E7BFF] to-transparent" />
          {t("legal.eyebrow")} · {LEGAL_VERSION}
        </span>
        <h1 data-testid="legal-title" className="mt-5 font-display text-4xl font-bold uppercase leading-[1] tracking-wide text-foreground sm:text-5xl">{doc.title}</h1>
        {lang !== "it" && i18n.language && !i18n.language.startsWith("en") && (
          <p data-testid="legal-lang-note" className="mt-4 text-xs text-[#FFC773]">{t("legal.languageNote")}</p>
        )}
        <p className="mt-6 text-sm leading-relaxed text-muted-foreground sm:text-base">{doc.intro}</p>

        <div className="glass-panel mt-10 space-y-9 rounded-2xl p-6 sm:p-9">
          {doc.sections.map((s, i) => (
            <section key={s.title} data-testid={`legal-section-${i + 1}`}>
              <h2 className="font-display text-base font-bold uppercase tracking-wide text-[#7FD4FF] md:text-lg">{s.title}</h2>
              {s.paragraphs?.map((p) => (
                <p key={p.slice(0, 40)} className="mt-3 text-sm leading-relaxed text-foreground/90">{p}</p>
              ))}
              {s.bullets && (
                <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-foreground/85">
                  {s.bullets.map((b) => <li key={b.slice(0, 60)}>{b}</li>)}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-5 text-sm">
          <Link to={`/${other}`} data-testid={`legal-link-${other}`} className="text-[#7FD4FF] underline-offset-4 hover:underline">
            {other === "privacy" ? t("legal.privacy") : t("legal.terms")}
          </Link>
          <a href={`mailto:${LEGAL_EMAIL}`} data-testid="legal-contact" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground">
            <Mail className="h-4 w-4" /> {LEGAL_EMAIL}
          </a>
        </div>
      </main>
    </Layout>
  );
}

export default function PrivacyPage() {
  return <LegalPage kind="privacy" />;
}
